<template>
  <v-card class="rounded-card card-shadow">
    <v-card-text>
      <v-row dense>
        <v-col cols="12" sm="7">
          <p class="mb-0">
            <span class="headline text--primary font-weight-bold">
              {{ finalizacao.comprador.pessoa.nome }}
            </span>
            <px-situacao-compra-tag :situacao="finalizacao.situacao"/>
            <px-gestao-entrega-produto-situacao
              show-truck-icon
              show-suffix-icon
              :situacao="finalizacao.situacaoEntrega"/>
          </p>
        </v-col>
        <v-col cols="12" sm="5" class="text-right">
          <template v-if="isPendenteFornecedor">
            <px-finalizacao-recusar-dialog :compra-id="compraId"
                                           :compra="finalizacao"
                                           @movimentar="onMovimentar"/>
            <px-finalizacao-aprovar-dialog :compra-id="compraId"
                                           :compra="finalizacao"
                                           v-if="isAprovacaoEnabled"
                                           @movimentar="onMovimentar"/>
            <px-finalizacao-confirm-approve-dialog
              :request-id="compraId"
              :compra-id="finalizacao.id"
              v-if="!isAprovacaoEnabled"
              :name="finalizacao.comprador.pessoa.nome"
              @update="onMovimentar"
            />
          </template>
          <px-detalhes-finalizacao-print-pdf v-if="showPrintPdf && !isRecusadoCancelado"
                                             :compra="finalizacao"
                                             :compra-id="compraId"/>
        </v-col>
      </v-row>
      <p class="mt-2">
        <v-icon>mdi-domain</v-icon>
        {{ finalizacao.comprador.pessoa.cnpj | cpfCnpj }}
      </p>
      <p>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <v-icon>mdi-map-marker</v-icon>
              {{ finalizacao.comprador.endereco | endereco }}
            </span>
          </template>
          <span>Endereço</span>
        </v-tooltip>
      </p>
      <div class="text--primary">
        <v-row dense>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Forma de pagamento</span>
            <div>
              <px-forma-pagamento :forma-pagamento="finalizacao.formaPagamento"
                                  :prazo-pagamento="finalizacao.prazoPagamento"/>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Tipo de frete</span>
            <div>
              {{ finalizacao.frete.tipoFrete }}
            </div>
          </v-col>
          <v-col cols="12" sm="3" v-if="finalizacao.usuario">
            <span class="caption font-weight-bold">Comprador responsável</span>
            <div>
              {{ finalizacao.usuario.nome }}
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Data de Faturamento</span>
            <div>
              {{ finalizacao.dataFaturamento | date | naoInformado }}
            </div>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Mensagem do comprador</span>
            <div>
              <px-show-large-text :large-text="finalizacao.mensagem"/>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Mensagem para o comprador</span>
            <div>
              <px-show-large-text :large-text="finalizacao.mensagemFornecedor"/>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Observação</span>
            <div>
              <px-show-large-text :large-text="finalizacao.observacaoExterna"/>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <span class="caption font-weight-bold">Modo de pagamento</span>
            <div>
              {{ modoPagamento }}
            </div>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" sm="3" v-if="isRecusadoCancelado">
            <span class="caption font-weight-bold">Motivo da recusa/cancelamento</span>
            <div>
              <px-show-large-text :large-text="finalizacao.motivoRecusa"/>
            </div>
          </v-col>
          <v-col cols="12" sm="3" v-if="!hasTransport">
            <span class="caption font-weight-bold">Transportadora</span>
            <div>
              {{ finalizacao.transportadora.pessoa.nome | naoInformado }}
            </div>
          </v-col>
          <v-col cols="12" sm="3" v-if="finalizacao.tipoNotaEntrada">
            <span class="caption font-weight-bold">Tipo de nota de entrada</span>
            <div>
              <px-span-tooltip top
                               :text="notaEntrada"
                               :tooltip="finalizacao.tipoNotaEntrada.descricao"/>
            </div>
          </v-col>
          <v-col cols="12" sm="3" v-if="finalizacao.cfop">
            <span class="caption font-weight-bold">CFOP da compra</span>
            <div>
              <px-span-tooltip
                top
                :text="cfop"
                :tooltip="`${finalizacao.cfop.codigo} - ${finalizacao.cfop.descricao}`"/>
            </div>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" sm="6">
            <span class="caption font-weight-bold">Nota fiscal</span>
            <div class="d-flex align-center" @click="onClickLabelNotaFiscal">
              {{ labelNotaFiscal }}
              <px-anexo small
                        v-model="finalizacao.notaFiscalFile"
                        title="Nota fiscal"
                        empty-button-text="Nota fiscal"
                        :download-base-path="downloadFilePath"
                        :upload-base-path="uploadBasePath"
                        :max="1"
                        :show-badge="false"
                        @loading="onNotaFiscalLoading"
                        @remove="onRemoveNotaFiscal">
                <template v-slot:activator="{ on }">
                  <v-btn id="notaFiscalBtn"
                         icon
                         small
                         v-on="on">
                    <v-icon small>mdi-paperclip</v-icon>
                  </v-btn>
                </template>
              </px-anexo>
            </div>
          </v-col>
          <v-col cols="12" sm="6" v-if="finalizacao.arquivos && finalizacao.arquivos.length">
            <span class="caption font-weight-bold">Anexos</span>
            <div>
              <px-anexo-chip :files="finalizacao.arquivos"
                             :download-base-path="downloadFilePath"/>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { SituacaoCompra, TipoFrete } from 'px-business-components';
import Vue from 'vue';
import DetalhesFinalizacaoPrintPdf from '@/modules/finalizacao/DetalhesFinalizacaoPrintPdf.vue';
import FinalizacaoConfirmApproveDialog from './FinalizacaoConfirmApproveDialog.vue';
import FinalizacaoAprovarDialog from './FinalizacaoAprovarDialog.vue';
import FinalizacaoRecusarDialog from './FinalizacaoRecusarDialog.vue';
import FinalizacaoService from './finalizacao-service';

export default {
  props: {
    finalizacao: Object,
    compraId: String,
    showPrintPdf: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loadingNotaFiscal: false,
    };
  },
  computed: {
    notaEntrada() {
      return Vue.filter('truncate')(this.finalizacao.tipoNotaEntrada.descricao, 20);
    },
    cfop() {
      return `${this.finalizacao.cfop.codigo} - ${Vue.filter('truncate')(this.finalizacao.cfop.descricao, 15)}`;
    },
    isAprovacaoEnabled() {
      return !this.finalizacao.entidadeComprador.pularAprovacaoFornecedor;
    },
    isPendenteFornecedor() {
      return this.finalizacao.situacao === SituacaoCompra.keys.PENDENTE_FORNECEDOR.key;
    },
    isRecusadoCancelado() {
      return this.finalizacao.situacao === SituacaoCompra.keys.RECUSADA.key
        || this.finalizacao.situacao === SituacaoCompra.keys.CANCELADA.key;
    },
    downloadFilePath() {
      return `/external/pedidos/${this.compraId}/file`;
    },
    uploadBasePath() {
      return `/external/pedidos/${this.compraId}/files`;
    },
    hasTransport() {
      return this.finalizacao.transportadora === null;
    },
    isCIF() {
      return this.finalizacao.frete.tipoFrete === TipoFrete.keys.CIF.key;
    },
    labelFrete() {
      return this.isCif ? 'Entrega' : 'Embarque';
    },
    modoPagamento() {
      return this.finalizacao.modoPagamento ? this.finalizacao.modoPagamento.descricao : 'Não informado';
    },
    labelNotaFiscal() {
      return this.finalizacao.notaFiscalFile && this.finalizacao.notaFiscalFile.length ? 'Atualizar nota fiscal' : 'Inserir nota fiscal';
    },
  },
  methods: {
    onMovimentar() {
      this.$emit('movimentar');
    },
    onRemoveNotaFiscal() {
      this.updateNotaFiscal();
    },
    onNotaFiscalLoading(loading) {
      if (!loading) {
        this.updateNotaFiscal();
      }
    },
    updateNotaFiscal() {
      this.loadingNotaFiscal = true;
      const payload = {
        id: this.finalizacao.id,
        notaFiscalFile: this.finalizacao.notaFiscalFile[0],
      };

      const message = payload.notaFiscalFile === undefined ? 'Nota fiscal removida' : 'Nota fiscal atualizada';

      FinalizacaoService.updateNotaFiscal(this.compraId, payload)
        .then(() => {
          this.$toast(message, { color: 'success' });
        })
        .finally(() => {
          this.loadingNotaFiscal = false;
        });
    },
    onClickLabelNotaFiscal() {
      const notaFiscalBtn = document.getElementById('notaFiscalBtn');
      if (notaFiscalBtn) {
        notaFiscalBtn.click();
      }
    },
  },
  created() {
    this.finalizacao.notaFiscalFile = this.finalizacao.notaFiscalFile
      ? [this.finalizacao.notaFiscalFile] : [];
  },
  components: {
    pxFinalizacaoAprovarDialog: FinalizacaoAprovarDialog,
    pxFinalizacaoRecusarDialog: FinalizacaoRecusarDialog,
    pxFinalizacaoConfirmApproveDialog: FinalizacaoConfirmApproveDialog,
    pxDetalhesFinalizacaoPrintPdf: DetalhesFinalizacaoPrintPdf,
  },
};
</script>
